import { useCallback } from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import { SlotInfo, CalDateInfo } from '../../../types/schedule.types';
import { SceneInfo } from '../../../types/scene.types';
import { buildDisplaySegments } from '../../../utils/calDateSlotsSortUtils';

export interface UseSlotInCalDateSortingParams {
  scheduleID: string | undefined;
  getDateSlots: (dateStr: string) => SlotInfo[];
  getDateCalDate: (dateStr: string) => CalDateInfo | undefined;
  getSceneInfo: (sceneID: string | undefined) => SceneInfo | undefined;
  updateCalDateSlotsSort: (
    dateStr: string,
    additionalSoltIds?: string[],
    excludeSoltIds?: string[],
    replaceIds?: string[],
    calDateShift?: string,
  ) => Promise<void>;
  refreshScheduleCalendarData: () => Promise<void>;
  setOptimisticCalDates: React.Dispatch<React.SetStateAction<CalDateInfo[]>>;
}

/**
 * 同日内 slot 排序：与 DayInfo 一致用 buildDisplaySegments 得到线性顺序，在扁平 ID 上做 arrayMove
 */
export const useSlotInCalDateSorting = ({
  scheduleID,
  getDateSlots,
  getDateCalDate,
  getSceneInfo,
  updateCalDateSlotsSort,
  refreshScheduleCalendarData,
  setOptimisticCalDates,
}: UseSlotInCalDateSortingParams) => {
  const handleInDateSorting = useCallback(
    async (activeId: string, overId: string, dateStr: string, bundleIdsParam?: string[] | null) => {
      if (!scheduleID) return;

      const daySlots = getDateSlots(dateStr);
      const calDate = getDateCalDate(dateStr);

      if (!calDate || daySlots.length === 0) return;

      const getSceneInfoMinimal = (sceneID: string | undefined) => {
        const s = getSceneInfo(sceneID);
        return s ? { sceneLocation: s.sceneLocation } : undefined;
      };

      const segments = buildDisplaySegments(
        daySlots,
        calDate.calDateSlotsSort,
        dateStr,
        getSceneInfoMinimal,
      );
      const orderedIds = segments.flatMap((s) => s.items);

      const activeRaw = String(activeId);
      const overRaw = String(overId);
      const bundleIds =
        bundleIdsParam && bundleIdsParam.length > 0 ? bundleIdsParam.map(String) : [activeRaw];
      const blockSet = new Set(bundleIds);
      const blockOrdered = orderedIds.filter((id) => blockSet.has(id));

      if (blockOrdered.length === 0) return;

      let newFlat: string[];

      if (blockOrdered.length === 1) {
        const oldIndex = orderedIds.indexOf(activeRaw);
        const newIndex = orderedIds.indexOf(overRaw);
        if (oldIndex === -1 || newIndex === -1) return;
        if (oldIndex === newIndex) return;
        newFlat = arrayMove([...orderedIds], oldIndex, newIndex);
      } else {
        const blockStart = orderedIds.indexOf(blockOrdered[0]);
        for (let i = 1; i < blockOrdered.length; i++) {
          if (orderedIds[blockStart + i] !== blockOrdered[i]) return;
        }
        if (blockSet.has(overRaw)) return;

        const withoutBlock = orderedIds.filter((id) => !blockSet.has(id));
        const insertAt = withoutBlock.indexOf(overRaw);
        if (insertAt === -1) return;

        newFlat = [...withoutBlock.slice(0, insertAt), ...blockOrdered, ...withoutBlock.slice(insertAt)];
      }

      setOptimisticCalDates((prev) =>
        prev.map((pd) => {
          if (!pd.calDateDt) return pd;
          if (pd.calDateDt.split('T')[0] !== dateStr) return pd;
          return { ...pd, calDateSlotsSort: newFlat };
        }),
      );
      try {
        await updateCalDateSlotsSort(dateStr, undefined, undefined, newFlat);
        refreshScheduleCalendarData();
      } catch (error) {
        console.error('更新任务顺序失败:', error);
        refreshScheduleCalendarData();
      }
    },
    [
      scheduleID,
      getDateSlots,
      getDateCalDate,
      getSceneInfo,
      refreshScheduleCalendarData,
      updateCalDateSlotsSort,
      setOptimisticCalDates,
    ],
  );

  return { handleInDateSorting };
};
